import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../local';

export interface NotificationItem {
  id: number;
  user_id: number;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  legal_deal_id: number | null;
  is_read: boolean;
  is_email_sent: boolean;
  created: string;
}

export interface NotificationContextType {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markAsRead: (id: number | number[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  refresh: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // ── Helper: get auth token ──
  const getToken = useCallback(() => localStorage.getItem('token'), []);

  // ── Helper: get current user ID ──
  // userdata di localStorage bisa pake field "user_id" (dari cookiedata)
  // atau "id" (dari data.user). Handle both.
  const getUserId = useCallback((): number | null => {
    try {
      const userdata = localStorage.getItem('userdata');
      if (userdata) {
        const parsed = JSON.parse(userdata);
        return parsed.user_id ? Number(parsed.user_id) :
               parsed.id ? Number(parsed.id) : null;
      }
    } catch {
      // ignore
    }
    return null;
  }, []);

  // ── Fetch unread count ──
  const refreshUnreadCount = useCallback(async () => {
    const userId = getUserId();
    const token = getToken();
    if (!userId || !token) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/notification/unread-count?token=${token}&user_id=${userId}`
      );
      const data = await res.json();
      if (data.success && isMountedRef.current) {
        setUnreadCount(data.count || 0);
      }
    } catch {
      // silent fail
    }
  }, [getUserId, getToken]);

  // ── Fetch notifications (first page) ──
  const refresh = useCallback(async () => {
    const userId = getUserId();
    const token = getToken();
    if (!userId || !token) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/notification/list?token=${token}&user_id=${userId}&page=1&limit=20`
      );
      const data = await res.json();
      if (data.success && isMountedRef.current) {
        setNotifications(data.data || []);
        setUnreadCount(
          (data.data || []).filter((n: NotificationItem) => !n.is_read).length
        );
      }
    } catch {
      // silent fail
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [getUserId, getToken]);

  // ── Mark as read (single or bulk) ──
  const markAsRead = useCallback(
    async (id: number | number[]) => {
      const token = getToken();
      const userId = getUserId();
      if (!token || !userId) return;

      const ids = Array.isArray(id) ? id : [id];

      try {
        const formData = new FormData();
        formData.append('token', token);
        formData.append('notification_ids', ids.join(','));

        const res = await fetch(`${API_BASE_URL}/notification/read`, {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        if (data.success) {
          // Update local state
          setNotifications((prev) =>
            prev.map((n) =>
              ids.includes(n.id) ? { ...n, is_read: true } : n
            )
          );
          setUnreadCount((prev) => Math.max(0, prev - ids.length));
        }
      } catch {
        // silent fail
      }
    },
    [getToken, getUserId]
  );

  // ── Mark all as read ──
  const markAllAsRead = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const formData = new FormData();
      formData.append('token', token);

      const res = await fetch(`${API_BASE_URL}/notification/read-all`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch {
      // silent fail
    }
  }, [getToken]);

  // ── Polling every 30 seconds ──
  useEffect(() => {
    // Reset mounted flag (handles React StrictMode double-mount)
    isMountedRef.current = true;

    // Don't poll if user is not logged in
    const token = getToken();
    const userId = getUserId();
    if (!token || !userId) return;

    // Initial fetch
    refresh();

    // Start polling
    pollingRef.current = setInterval(() => {
      refreshUnreadCount();
    }, 30000);

    return () => {
      isMountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [refresh, refreshUnreadCount, getToken, getUserId]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        refresh,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
